import { API_BASE_URL, STORAGE_KEY } from "@/appSettings";
import { UserRole } from "@/enums";
import { FieldValues, LiteralUnion, Path, UseFormSetError, UseFormSetValue } from "react-hook-form";
import { AppDispatch } from "@/store";
import { setCurrentUserPermissions } from "@/store/user/slice";
import { jwtDecode } from "jwt-decode";
import dayjs, { Dayjs } from "dayjs";
import { isTimeOnly } from "./validate";
import { AmPm } from "@/enums";
import { VariantType } from "notistack";
import { GridPaginationModel } from "@mui/x-data-grid";
import { t } from "i18next";

const { token } = STORAGE_KEY;
const NON_FIELD_ERRORS = "non_field_errors";

export const getToken = (): IAuth | null => {
    const storeString = localStorage.getItem(token) || "";
    return storeString ? JSON.parse(storeString) : null;
};

export const setToken = (payload: IAuth) => {
    localStorage.setItem(token, JSON.stringify(payload));
};

export const removeToken = (): void => {
    localStorage.removeItem(token);
};

export const copyContent = (text: string) => navigator.clipboard.writeText(text);

const MEDIA_HOST = API_BASE_URL.replace(/\/api\/?$/, "");
export const resolveMediaUrl = (url: string) => (/^https?:\/\//i.test(url) ? url : `${MEDIA_HOST}${url}`);

const API_URL = new URL(API_BASE_URL, window.location.origin);
const API_PATH = API_URL.pathname.replace(/\/$/, "");
export const resolveApiUrl = (url: string) => {
    const { pathname, search } = new URL(url, API_URL.origin);
    return `${API_URL.origin}${pathname.startsWith(`${API_PATH}/`) ? "" : API_PATH}${pathname}${search}`;
};

export const getFirstDayOfMonth = (month: number, year?: number) => {
    const now = year ? new Date(year, month) : new Date();
    return new Date(now.getFullYear(), month, 1).getDay();
};

export const daysInMonth = (month?: number) => {
    const now = new Date();
    return new Date(now.getFullYear(), (month ? month : now.getMonth()) + 1, 0).getDate();
};

export const renderDates = (month: number, year?: number) => {
    const days = daysInMonth(month);
    const dayOffset = getFirstDayOfMonth(month, year);
    const cal = [];
    let week = [];

    for (let date = 1 - dayOffset; date <= days; date++) {
        if (date <= 0) week.push(0);
        else week.push(date);

        const wL = week.length;
        if (wL === 7) {
            cal.push(week);
            week = [];
        }
        if (date === days && wL !== 0) {
            for (let i = wL; i < 7; i++) week.push(0);
            cal.push(week);
        }
    }
    return cal;
};

export const getShortDate = (date: Date) => {
    const month = date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1;
    const newDate = date.getDate() < 10 ? "0" + date.getDate() : date.getDate();
    return date.getFullYear() + "-" + month + "-" + newDate;
};

export const getTime = (date: Dayjs) => {
    const hour = Number(date.hour()) < 10 ? "0" + Number(date.hour()) : Number(date.hour());
    const minute = Number(date.minute()) < 10 ? "0" + Number(date.minute()) : Number(date.minute());
    return hour + ":" + minute;
};

export const isApiError = (error: unknown): error is IApiError => {
    return (error as IApiError).status !== undefined || (error as IApiError).data !== undefined;
};

export const isAbortError = (error: unknown): boolean => {
    if (!error) return false;
    const e = error as { status?: string; error?: string; name?: string; code?: string };
    if (e.status === "FETCH_ERROR" && typeof e.error === "string" && e.error.includes("AbortError")) return true;
    if (e.name === "AbortError") return true;
    if (e.code === "ERR_CANCELED") return true;
    return false;
};

export const simplifyError = (obj: IErrorData | undefined) => {
    return (obj && Object.values(obj)?.flat()) || [];
};

export const getQueryString = (filter: IFilters) => {
    return (
        new URLSearchParams(
            Object.entries(filter)
                .filter(([, value]) => value != null)
                .map(([key, value]) => [key, value.toString()]),
        ).toString() || ""
    );
};

export const isSuperAdmin = (role: string) => role === UserRole.SuperAdmin;
export const isAdmin = (role: string) => role === UserRole.Admin;
export const isResearcher = (role: string) => role === UserRole.Researcher;

export const getUserQueryString = (filter: IFilters) => {
    return (
        new URLSearchParams(
            Object.entries(filter)
                .filter(([, value]) => value != null)
                .map(([key, value]) => [key, value.toString()]),
        ).toString() || ""
    );
};

export const getChangedFields = <T extends Record<string, unknown>>(initialValues: T, currentValues: T): Partial<T> => {
    const changes: Partial<T> = {};

    for (const key of Object.keys(currentValues)) {
        const typedKey = key as keyof T;

        if (currentValues[typedKey] !== initialValues[typedKey]) {
            changes[typedKey] = currentValues[typedKey];
        }
    }

    return changes;
};

export const getFieldErrors = (
    type: LiteralUnion<
        | "max"
        | "value"
        | "pattern"
        | "onBlur"
        | "onChange"
        | "min"
        | "disabled"
        | "maxLength"
        | "minLength"
        | "required"
        | "valueAsNumber"
        | "valueAsDate"
        | "setValueAs"
        | "validate"
        | "shouldUnregister"
        | "deps",
        string
    >,
) => {
    switch (type) {
        case "max": {
            return "The value exceeds the maximum";
        }
        case "value": {
            return "Invalid value";
        }
        case "pattern": {
            return "The value does not match the required pattern";
        }
        case "onBlur": {
            return "Error occurred during onBlur event";
        }
        case "onChange": {
            return "Error occurred during onChange event";
        }
        case "min": {
            return "The value is below the minimum";
        }
        case "disabled": {
            return "This field is disabled";
        }
        case "maxLength": {
            return "The value exceeds the maximum";
        }
        case "minLength": {
            return "The value is below the minimum";
        }
        case "required": {
            return "This field is required";
        }
        case "valueAsNumber": {
            return "The value must be a number";
        }
        case "valueAsDate": {
            return "The value must be a date";
        }
        case "setValueAs": {
            return "Error in setting the value";
        }
        case "validate": {
            return "Validation error";
        }
        case "shouldUnregister": {
            return "The field should be unregistered";
        }
        case "deps": {
            return "Error in field dependencies";
        }
        default: {
            return "Unknown error";
        }
    }
};

// Function to decode token and dispatch permissions
export const handleTokenAndSetPermissions = (token: string | null, dispatch: AppDispatch) => {
    if (token) {
        try {
            // Decode the token
            const decodedToken: IAuthToken = jwtDecode(token);
            const role = decodedToken.role;

            if (role === UserRole.Patient) {
                return false;
            } else {
                // Dispatch the permissions from the decoded token
                dispatch(setCurrentUserPermissions(decodedToken.permissions));
                return true;
            }
        } catch (error) {
            console.error("Error decoding token:", error);
        }
    }
};

// Pass to setPaginationModel when a search/filter changes, so the grid goes back to its first page
export const toFirstPage = (model: GridPaginationModel): GridPaginationModel =>
    model.page ? { ...model, page: 0 } : model;

export const DataGridStyle = {
    // prevent ugly focus cell when clicked
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
        outline: "none !important",
    },
    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
        outline: "none",
    },
    "& .MuiDataGrid-row:hover": {
        cursor: "pointer",
    },
};

export const DataGridStyleWithoutCursor = {
    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
        outline: "none !important",
    },
    "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus-within": {
        outline: "none",
    },
};

export const dateFormatter = (datetime: string) => {
    const date = new Date(datetime);
    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric",
    };
    return date.toLocaleString("en-GB", options).replace(",", "");
};

export const datetimeFormatter = (datetime: string) => {
    const date = new Date(datetime);
    const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
    };
    return date.toLocaleString("en-GB", options).replace(",", "");
};

export const timeFormatter = (datetime: string) => {
    const date = new Date(isTimeOnly(datetime) ? `1970-01-01T${datetime}Z` : datetime);
    const options: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
        timeZone: "UTC",
    };
    return date.toLocaleString("en-GB", options).replace(",", "");
};

export const timeFormatterWithoutSecond = (datetime: string) => {
    const date = new Date(isTimeOnly(datetime) ? `1970-01-01T${datetime}Z` : datetime);
    const options: Intl.DateTimeFormatOptions = {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
        timeZone: "UTC",
    };
    return date.toLocaleString("en-GB", options).replace(",", "");
};

export const formatErrorDetailString = (holder: string): string => {
    return holder
        .replace(/ErrorDetail\(string='(.*?)', code='(.*?)'\)/g, "{'string': '$1', 'code': '$2'}")
        .replace(/'/g, "'");
};

export const formatDateTime = (datetime: string) => {
    const scheduledDate = dayjs(datetime);
    const am_pm = scheduledDate.hour() < 12 ? AmPm.am : AmPm.pm;
    const time = scheduledDate.format(`hh:mm`) + " " + am_pm;
    const day = scheduledDate.format("ddd");
    const date = scheduledDate.format("D MMMM YYYY");
    const year = scheduledDate.format("YYYY");
    const date_date = scheduledDate.format("D");
    const month = scheduledDate.format("MMM");
    return { day, date, time, am_pm, month, date_date, year };
};

export const formatDate = (date: Dayjs | undefined) => {
    return dayjs(date).format("YYYY-MM-DD");
};

export const urgencyLevel = [
    {
        urgency_value: "low",
        urgency_name: "1 - Low",
        name_short: "Low",
        color: "green",
    },
    {
        urgency_value: "moderate",
        urgency_name: "2 - Moderate",
        name_short: "Moderate",
        color: "olive",
    },
    {
        urgency_value: "high",
        urgency_name: "3 - High",
        name_short: "High",
        color: "orange",
    },
    {
        urgency_value: "major",
        urgency_name: "4 - Major",
        name_short: "Major",
        color: "red",
    },
    {
        urgency_value: "catastrophic",
        urgency_name: "5 - Catastrophic",
        name_short: "Catastrophic",
        color: "red",
    },
];

export const feedbackStatus = [
    {
        value: "pending",
        name: "Pending",
        color: "#8C8C8C",
        bg: "#F5F5F5",
    },
    {
        value: "solving",
        name: "Solving",
        color: "#3452C1",
        bg: "#F1F5FD",
    },
    {
        value: "solved",
        name: "Solved",
        color: "#389E0D",
        bg: "#F6FFED",
    },
    {
        value: "dropped",
        name: "Dropped",
        color: "#CF1322",
        bg: "#FFF1F0",
    },
];

export const getFeedbackStatusStyle = (value: string) =>
    feedbackStatus.find((s) => s.value === value) ?? feedbackStatus[0];

export const isObject = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && !Array.isArray(value) && value !== null;
};

export const errorShowToast = (
    err: unknown,
    fn: (message: string, variant?: VariantType) => void,
    fieldLabels?: Record<string, string>,
): void => {
    if (isApiError(err) && err.data?.message) {
        const message = err.data?.message;
        if (typeof message === "string") {
            fn(message, err.data?.type);
        } else if (Array.isArray(message)) {
            if (isObject(message)) {
                const errorMessages = message as Record<string, string>;
                const messageKeys = message.keys();
                for (const errMsg in errorMessages) {
                    const fieldName = messageKeys[errMsg as keyof typeof fieldLabels] || errMsg;
                    const errorDetail = errorMessages[errMsg as keyof typeof errorMessages];
                    fn(
                        fieldName !== NON_FIELD_ERRORS ? `${fieldName}: ${errorDetail}` : `${errorDetail}`,
                        err.data?.type ?? "error",
                    );
                }
            } else {
                // reaching here: array of strings
                for (let i = 0; i < message.length; i++) {
                    fn(message[i], err.data?.type ?? "error");
                }
            }
        }
        if (isObject(message)) {
            for (const key in message) {
                const fieldErrors = message[key];

                if (Array.isArray(fieldErrors)) {
                    for (const errorItem of fieldErrors) {
                        if (typeof errorItem === "string") {
                            fn(key === NON_FIELD_ERRORS ? errorItem : `${key}: ${errorItem}`, err.data?.type);
                        } else if (isObject(errorItem)) {
                            // Deep nested structure like in your example
                            for (const nestedKey in errorItem) {
                                const nestedErrors = errorItem[nestedKey];
                                if (Array.isArray(nestedErrors)) {
                                    for (const nestedMessage of nestedErrors) {
                                        fn(`${nestedKey}: ${nestedMessage}`, err.data?.type);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    } else if (!isAbortError(err)) {
        fn(t("deviceModel.showToast.unknownError"), "error");
    }
};

/**
 * Attaches DRF field errors (`{ field: ["message"] }`) to the matching form
 * inputs so they render inline instead of as an opaque toast.
 * Returns true when at least one error was placed on a field; callers should
 * fall back to `errorShowToast` when it returns false.
 */
export const setApiFieldErrors = <T extends FieldValues>(
    err: unknown,
    setError: UseFormSetError<T>,
    formFields: readonly string[],
): boolean => {
    if (!isApiError(err)) return false;
    const message = err.data?.message;
    if (!isObject(message)) return false;

    let handled = false;
    for (const key in message) {
        if (!formFields.includes(key)) continue;
        const detail = message[key];
        const text = Array.isArray(detail) ? detail[0] : detail;
        if (typeof text !== "string") continue;
        setError(key as Path<T>, { type: "server", message: text });
        handled = true;
    }
    return handled;
};

export const riskLabels = [
    {
        value: -3,
        label: "High",
        color: "red",
    },
    {
        value: -2,
        label: "Moderate",
        color: "orange",
    },
    {
        value: -1,
        label: "Low",
        color: "green",
    },
    {
        value: 0,
        label: "Low",
        color: "green",
    },
    {
        value: 1,
        label: "Low",
        color: "green",
    },
    {
        value: 2,
        label: "Moderate",
        color: "orange",
    },
    {
        value: 3,
        label: "High",
        color: "red",
    },
];

export const formatMinutesToHours = (mins: number) => {
    const hours = Math.floor(mins / 60);
    mins -= hours * 60;

    const result = [];
    if (hours > 0) result.push(`${hours} hour${hours > 1 ? "s" : ""}`);
    if (mins > 0) result.push(` ${mins} minutes`);

    return result || "-";
};

export const formatDaysToYears = (days: number) => {
    const years = Math.floor(days / 365);
    days -= years * 365;

    const months = Math.floor(days / 30);
    days -= months * 30;

    const result = [];

    if (years > 0) result.push(`${years} year${years > 1 ? "s" : ""}`);
    if (months > 0) result.push(`${months} month${years > 1 ? "s" : ""}`);
    if (days > 0) result.push(`${days} day${days > 1 ? "s" : ""}`);

    return result.join(", ") || "0 days";
};

export const operators: {
    operator: string;
    overlap: boolean;
    size: number;
}[] = [
    {
        operator: ">",
        overlap: false,
        size: 1,
    },

    {
        operator: ">=",
        overlap: true,
        size: 2,
    },
    {
        operator: "<",
        overlap: false,
        size: 3,
    },
    {
        operator: "<=",
        overlap: true,
        size: 4,
    },
];

export const indiceTypes = [
    {
        name: "Urine Output",
        value: "US",
    },
    {
        name: "Pain Score",
        value: "PS",
    },
    {
        name: "Age",
        value: "AS",
    },
    {
        name: "BMI",
        value: "BS",
    },
    {
        name: "No of Comorbidity",
        value: "CS",
    },
];

export const getIndiceName = (indice: IndiceType) => {
    switch (indice) {
        case "US":
            return t("riskScoring.indiceType.US");
        case "PS":
            return t("riskScoring.indiceType.PS");
        case "AS":
            return t("riskScoring.indiceType.AS");
        case "BS":
            return t("riskScoring.indiceType.BS");
        case "CS":
            return t("riskScoring.indiceType.CS");
        default:
            break;
    }
};

export const getUrineColorRange = () => {
    return [
        {
            level: 1,
            color: "#FBFCEE",
            name: t("riskScoring.urineRange.level_1"),
        },
        {
            level: 2,
            color: "#F6F5B5",
            name: t("riskScoring.urineRange.level_2"),
        },
        {
            level: 3,
            color: "#F1EB11",
            name: t("riskScoring.urineRange.level_3"),
        },
        {
            level: 4,
            color: "#FECE0B",
            name: t("riskScoring.urineRange.level_4"),
        },
        {
            level: 5,
            color: "#BE7002",
            name: t("riskScoring.urineRange.level_5"),
        },
        {
            level: 6,
            color: "#9B6000",
            name: t("riskScoring.urineRange.level_6"),
        },
        {
            level: 7,
            color: "#9E392B",
            name: t("riskScoring.urineRange.level_7"),
        },
    ];
};

export const determinePrecisionPoint = (array: string[]) => {
    // takes an array of strings("0.11, 2.22, 294.1111")
    // TO determine the largest(most precise) decimal place (0.0001)
    // line 2: .toPrecision can't take 0 (starting from 2 is only whole number)
    // replace last number with a 1 - smallest unit gap

    if (array.length > 0) {
        const decimalPlaces = array.map((x) =>
            x != null ? (x.toString().includes(".") ? x.toString().split(".")[1].length : 0) : 0,
        );
        const arrayPrecisionPoint = Math.max(...decimalPlaces);
        let precisionString = "";
        if (arrayPrecisionPoint === 0) {
            precisionString = Number(0).toFixed(2);
        } else {
            precisionString = Number(0).toFixed(arrayPrecisionPoint);
        }
        if (arrayPrecisionPoint === 2 && precisionString === "0.00") return "0.01";
        if (precisionString === "0.00") {
            return "1.0";
        } else {
            return precisionString.substring(0, precisionString.length - 1) + "1";
        }
    } else {
        return "1";
    }
};

export const centerGridContent = {
    display: "flex",
    paddingX: 2,
    paddingY: 2,
    alignItems: "center",
};

export const gridHeader = {
    borderBottom: 1,
    borderColor: "primary.4",
    fontWeight: 500,
    color: "primary.7",
    paddingY: 1.5,
    paddingX: 2,
};

export const getSubParamIdFromRiskRow = (
    subparameter:
        | undefined
        | string
        | {
              subparameter_id: string;
              sub_parameter_name: string;
          },
) => {
    // doing this because the object field keeps changing from backend
    return subparameter === undefined
        ? ""
        : typeof subparameter === "string"
          ? subparameter
          : subparameter.subparameter_id;
};

export const primaryPieOption = {
    elements: {
        point: {
            radius: 8,
            hoverRadius: 14,
        },
    },
    options: {
        interaction: {
            intersect: false,
            mode: "index",
        },
    },
    scales: {
        y: {
            min: 10,
            max: 200,
        },
    },
};

export const handleNumberInput = (input: string, setValue: UseFormSetValue<any>, fieldName: string) => {
    // Allow digits only
    if (input === "" || /^\d*\.?\d*$/.test(input)) {
        setValue(fieldName, input);
    }
};

type HeightConversionType = "toMeter" | "toCentimeter";

export const patientHeightConverter = (height: number | string, conversionType: HeightConversionType) => {
    //Format height to number
    const convertHeightToNumber = typeof height === "string" ? Number(height) : height;
    const conversionFormula = 100;

    if (conversionType === "toMeter") {
        return (convertHeightToNumber / conversionFormula).toFixed(2);
    } else if (conversionType === "toCentimeter") {
        return (convertHeightToNumber * conversionFormula).toFixed(0);
    }

    return 0;
};

export const getTodaysDate = () => {
    const plusEightHours = 8 * 60 * 60 * 1000;
    const dateTimeWithUTC8 = Date.now() + plusEightHours;
    const dateWithUTC8 = new Date(dateTimeWithUTC8).toISOString().split("T")[0];

    return dateWithUTC8;
};

export const handleAgeInput = (dob: string | Date): number => {
    if (!dob) return 0;

    const birthDate = new Date(dob);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    return age;
};
